import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import bindMethods from 'auto-bind';
import { faTimesCircle, faPrint, faVideo } from '@fortawesome/free-solid-svg-icons';

import { clearSelections as clearDownDistanceSelections } from '../state/downDistance';
import { clearSelections as clearFieldZoneSelections } from '../state/fieldZone';
import { resetTeamSeasonGameSelection } from '../state/teamSeasonGameSelection';
import { getQlikAppId } from '../state/selectors/getQlikAppId';
import { getQlikApp, createHyperCubeDefForField } from '../utils/qlik';
import { launchThunder } from '../api/thunder';
import { setThunderUrl } from '../state/settings';
import { getPurposeValue, getPageValue } from '../state/selectors/getNavigationValue';
import { addDefaultSelections } from '../state/defaultSelections';
import { reapplyDefaultSelections } from '../utils/defaultSelections';

import ActionButton from './ActionButton';
import QlikBreadcrumbs from './QlikBreadcrumbs';

import { PLAY_ID } from '../constants/fields';

const QlikContentHeaderContainer = styled.div`
  height: 80px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 20px;
`;

const Column = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: ${props => props.justifyContent ? props.justifyContent : ''};
`;

const Iframe = styled.iframe`
  position: absolute;
  width: 0;
  height: 0;
  border: none;
`;

class QlikContentHeader extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);
    this.hyperCubeDef = createHyperCubeDefForField(PLAY_ID);
  }

  async handleClearAll() {
    const {
      qlikAppId, clearDownDistanceSelections, clearFieldZoneSelections, defaultSelections,
      resetTeamSeasonGameSelection,
    } = this.props;
    const app = await getQlikApp(qlikAppId);

    await app.clearAll();

    reapplyDefaultSelections(app, defaultSelections);
    clearDownDistanceSelections();
    clearFieldZoneSelections();
    resetTeamSeasonGameSelection();
  }

  async handleLaunchVideos() {
    const {
      getPageValue,
      getPurposeValue,
      qlikAppId,
      setThunderUrl,
      thunderDefault,
      authToken,
    } = this.props;
    const app = await getQlikApp(qlikAppId);
    const results = await app.createCube(this.hyperCubeDef);
    const playIds = results.layout.qHyperCube.qDataPages[0].qMatrix.map(d => d[0].qText);
    const appType = (thunderDefault) ? 'thunder' : 'thunderCloud';

    const url = await launchThunder(
      playIds,
      appType,
      getPurposeValue.label,
      getPageValue.label,
      authToken,
    );

    setThunderUrl(url);

    const launchWindow = window.open(url, '_blank', 'dependent=yes,alwaysLowered=yes,top=1,left=1,width=1,height=1');
    launchWindow.close();
  }

  handlePrint() {
    const { team, pageName } = this.props;
    const pageTitle = team ? `${team} - ${pageName}` : pageName;
    document.title = `Dashboard - ${pageTitle}`;
    window.print();
  }

  render() {
    const { t, thunderDefault, thunderUrl } = this.props;

    return (
      <QlikContentHeaderContainer>
        <Column>
          <QlikBreadcrumbs />
        </Column>
        <Column justifyContent="flex-end">
          <ActionButton
            id="ActionButton.ClearAll"
            text={t('ContentArea.Header.ActionButtons.ClearAll')}
            icon={faTimesCircle}
            action={this.handleClearAll}
          />
          <ActionButton
            id="ActionButton.Print"
            text={t('ContentArea.Header.ActionButtons.Print')}
            icon={faPrint}
            action={this.handlePrint}
          />
          <ActionButton
            id="ActionButton.LaunchThunder"
            text={(thunderDefault) ? t('ContentArea.Header.ActionButtons.LaunchThunder') : t('ContentArea.Header.ActionButtons.LaunchThunderCloud')}
            icon={faVideo}
            action={this.handleLaunchVideos}
          />
        </Column>

        {thunderUrl && <Iframe title="thunder" src={thunderUrl} />}
      </QlikContentHeaderContainer>
    );
  }
}

QlikContentHeader.propTypes = {
  clearDownDistanceSelections: PropTypes.func.isRequired, // supplied from Redux
  clearFieldZoneSelections: PropTypes.func.isRequired, // supplied from Redux
  getPageValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  getPurposeValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  qlikAppId: PropTypes.string.isRequired, // supplied from Redux
  t: PropTypes.func.isRequired, // supplied from withTranslation
  setThunderUrl: PropTypes.func.isRequired, // supplied from Redux
  thunderDefault: PropTypes.bool.isRequired, // supplied from Redux
  thunderUrl: PropTypes.string.isRequired, // supplied from Redux
  defaultSelections: PropTypes.object.isRequired,
  resetTeamSeasonGameSelection: PropTypes.func.isRequired,
  pageName: PropTypes.string.isRequired, // supplied from Redux
  team: PropTypes.string.isRequired, // supplied from Redux
  authToken: PropTypes.string.isRequired, // supplied from Redux
};

const mapStateToProps = state => ({
  getPageValue: getPageValue(state),
  getPurposeValue: getPurposeValue(state),
  qlikAppId: getQlikAppId(state),
  thunderDefault: state.settings.thunder.default,
  thunderUrl: state.settings.thunder.url,
  defaultSelections: state.defaultSelections,
  authToken: state.auth.authToken,
  pageName: state.navigationSelection.page.navPageid,
  team: state.teamSeasonGameSelection.team,
});

const mapDispatchToProps = {
  clearDownDistanceSelections,
  clearFieldZoneSelections,
  setThunderUrl,
  addDefaultSelections,
  resetTeamSeasonGameSelection,
};

const TranslatedComponent = withTranslation()(QlikContentHeader);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TranslatedComponent);
